import React, { useEffect, useState } from "react";
import "./MotivationDashboard.css";
import { Fetch } from "../../shared/Fetch";

interface Branch {
    name: string;
    code: string;
    min: number;
    max: number;
    sept: number;
    color: string;
}

function BranchCard({ branch }: { branch: any }) {
    const percent = Math.min((branch.sept / branch.max) * 100, 100);
    const reachedMax = branch.sept >= branch.max;
    const reachedMin = branch.sept >= branch.min;

    return (
        <div className="branch-card">
            <div
                className="branch-glow"
                style={{ background: branch.color }}
            />

            <div className="branch-top">
                <div className="branch-avatar" style={{ color: branch.color }}>
                    {branch.code}
                </div>

                <div>
                    <div className="branch-name">{branch.name}</div>
                    <div className="branch-team">
                        {/* {branch.employees} team members */}
                    </div>
                </div>

                <div className="branch-result">
                    {reachedMax ? "🏆" : reachedMin ? "🔥" : "💪"}
                </div>
            </div>

            <div className="branch-number">
                <strong>{branch.sept}</strong>
                <span> / {branch.min} PO</span>
            </div>

            <div className="progress-track">
                <div
                    className="progress-fill"
                    style={{
                        width: `${percent}%`,
                        background: `linear-gradient(90deg, ${branch.color}, #fff)`,
                    }}
                />
            </div>

            <div className="branch-meta">
                <span>Min {branch.min}</span>
                <span>Max {branch.max}</span>
            </div>
        </div>
    );
}

export default function MotivationDashboard() {
    const [branchPerformance, setBranchPerformance] = useState<Array<Branch>>([]);
    const loadTableDataBranches = async () => {
        const response = await Fetch("Requirement_MAST/octTargetBranch", "GET", null, null, null);

        if (response) {
            const data: Branch[] = await response.json();
            setBranchPerformance(data);
        }
    };

    const totalSeptember = branchPerformance.reduce((sum, b) => sum + (b?.sept || 0), 0);

    const octoberTarget = 425;
    const septemberProgress = Math.min(
        Math.round((totalSeptember / octoberTarget) * 100),
        100
    );

    useEffect(() => {
        loadTableDataBranches();
    }, []);

    return (
        <section className="motivation-dashboard">
            <div className="dashboard-noise" />

            {/* Header */}
            <header className="dashboard-header">
                <div>
                    <div className="eyebrow">
                        <span className="live-dot" />
                        OCTOBER 2026 • COMPANY MISSION
                    </div>

                    <h1>
                        We came close in September and gave us momentum.
                        <br />
                        <span>Let's turn it into 425 this OCTOBER.</span> 🚀
                    </h1>

                    <p>
                        7 branches. One team. One mission.
                        <strong> 425 Purchase Orders.</strong>
                    </p>
                </div>

                <div className="celebration-badge">
                    <div className="badge-icon">🎉</div>
                    <div>
                        <small>SEPTEMBER</small>
                        <strong>208 / 251 PO's ACHIEVED 🚀</strong>
                    </div>
                </div>
            </header>

            {/* Main Mission */}
            <div className="mission-layout">

                {/* Left: Big target */}
                <div className="hero-target">

                    <div className="target-orbit orbit-one" />
                    <div className="target-orbit orbit-two" />

                    <div className="target-content">
                        <span className="target-label">
                            OCTOBER TARGET
                        </span>

                        <div className="target-number">
                            {octoberTarget}
                        </div>

                        <div className="target-po">
                            PURCHASE ORDERS
                        </div>

                        <div className="target-message">
                            <span>🏁</span>
                            The board is reset.
                            <br />
                            <strong>Your next win starts today.</strong>
                        </div>
                    </div>
                </div>

                {/* Center: Stats */}
                <div className="mission-stats">

                    <div className="stat-card gold">
                        <div className="stat-icon">🏆</div>
                        <div>
                            <span>SEPTEMBER</span>
                            <strong>{208}</strong>
                            <small>POs achieved</small>
                        </div>
                    </div>

                    <div className="stat-card green">
                        <div className="stat-icon">🔥</div>
                        <div>
                            <span>OCTOBER</span>
                            <strong>425</strong>
                            <small>fresh opportunities</small>
                        </div>
                    </div>

                    <div className="stat-card purple">
                        <div className="stat-icon">👥</div>
                        <div>
                            <span>BRANCHES</span>
                            <strong>07</strong>
                            <small>teams competing</small>
                        </div>
                    </div>

                    <div className="momentum-card">
                        <div className="momentum-header">
                            <span>SEPTEMBER MOMENTUM</span>
                            <strong>{82.8}%</strong>
                        </div>

                        <div className="momentum-bar">
                            <div
                                style={{ width: `${82.8}%` }}
                            />
                        </div>

                        <p>
                            Every PO counts. Every person matters.
                        </p>
                    </div>

                </div>

                {/* Right: Motivation */}
                <div className="motivation-panel">

                    <div className="quote-mark">“</div>

                    <h2>
                        Your September
                        <span> story isn't finished.</span>
                    </h2>

                    <p>
                        ✦ If you were a top performer — keep the momentum.
                        <br />✦ If September wasn't your month — October is your
                        comeback.
                    </p>

                    <div className="motivation-list">
                        <div>
                            <span>🏆</span>
                            <p>
                                <strong>Top performers</strong>
                                <small>Raise the bar.</small>
                            </p>
                        </div>

                        <div>
                            <span>🔥</span>
                            <p>
                                <strong>Almost there?</strong>
                                <small>Push a little harder.</small>
                            </p>
                        </div>

                        <div>
                            <span>🚀</span>
                            <p>
                                <strong>Missed September?</strong>
                                <small>Your comeback starts now.</small>
                            </p>
                        </div>
                    </div>

                    <button className="mission-button">
                        LET'S OWN OCTOBER
                        <span>→</span>
                    </button>

                </div>
            </div>

            {/* Branches */}
            <div className="branches-section">
                <div className="section-title">
                    <div>
                        <span>THE 7-BRANCH CHALLENGE</span>
                        <h3>September Champions → October Contenders</h3>
                    </div>

                    <div className="legend">
                        <span><i className="green-dot" /> Target zone</span>
                        <span><i className="orange-dot" /> Keep pushing</span>
                    </div>
                </div>

                <div className="branches-grid">
                    {branchPerformance.map((branch: Branch) => (
                        <BranchCard
                            key={branch.code}
                            branch={branch}
                        />
                    ))}
                </div>
            </div>

            {/* Bottom Banner */}
            <div className="bottom-banner">
                <div className="banner-left">
                    <span>⚡</span>
                    <div>
                        <strong>425 POs.</strong>
                        <small>One company. One October.</small>
                    </div>
                </div>

                <div className="banner-center">
                    SEPTEMBER PROVED WE CAN.
                    <strong>OCTOBER IS WHERE WE REPEAT IT.</strong>
                </div>

                <div className="banner-right">
                    LET'S GO
                    <span>→</span>
                </div>
            </div>
        </section>
    );
}
